import { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Footer } from '../shared/Footer';

interface ClientData {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  email_verified: boolean;
  last_login?: string;
  created_at: string;
}

interface ClientDashboardProps {
  initialClient: ClientData;
  onLogout: () => void;
}

type DashboardTab = 'orders' | 'gift-ideas' | 'addresses' | 'subscriptions';

export function ClientDashboard({ initialClient, onLogout }: ClientDashboardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState<ClientData>(initialClient);
  const [isLoading, setIsLoading] = useState(false);
  const [cachedData, setCachedData] = useState<Record<string, any>>({});

  const validPaths = ['/customer/dashboard/orders', '/customer/dashboard/gift-ideas', '/customer/dashboard/addresses', '/customer/dashboard/subscriptions'];
  
  useEffect(() => {
    if (!validPaths.includes(location.pathname)) {
      navigate('/customer/dashboard/orders', { replace: true });
    }
  }, [location.pathname, navigate]);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await fetch('/api/customer/logout', { method: 'POST' });
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileUpdate = (updatedClient: ClientData) => {
    setClient(updatedClient);
  };

  const handleTabChange = (path: string) => {
    navigate(path);
  };

  const handleDataLoaded = (tabName: string, data: any) => {
    setCachedData(prev => ({
      ...prev,
      [tabName]: data
    }));
  };

  const tabs = [
    { path: '/customer/dashboard/orders', label: 'My Orders', icon: '📄' },
    { path: '/customer/dashboard/gift-ideas', label: 'My Gifts Ideas', icon: '🎁' },
    { path: '/customer/dashboard/addresses', label: 'Addresses', icon: '📍' },
    { path: '/customer/dashboard/subscriptions', label: 'My Subscriptions', icon: '💳' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-normal text-purple-600" style={{ color: '#8F6E98' }}>
                  birdie
                </span>
                <span className="text-xs font-normal text-green-600 tracking-wider" style={{ color: '#7A8C6C' }}>
                  GIFTS
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome <strong>{client.firstname}</strong>
              </span>
              <span className="text-gray-300">|</span>
              <button
                onClick={handleLogout}
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50 flex items-center cursor-pointer"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {isLoading ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-80">
            <div className="bg-white rounded-2xl shadow-sm p-6" style={{ borderRadius: '24px' }}>
              <nav className="space-y-3">
                {tabs.map((tab) => (
                  <button
                    key={tab.path}
                    onClick={() => handleTabChange(tab.path)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors flex items-center ${
                      isActive(tab.path)
                        ? 'text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    style={{
                      backgroundColor: isActive(tab.path) ? '#4a5568' : 'transparent',
                      borderRadius: '12px'
                    }}
                  >
                    <span className="mr-3">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm">
              <Outlet context={{ client, cachedData, handleDataLoaded }} />
            </div>
          </div>
        </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}